import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { ChevronLeft } from "lucide-react";
import {
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
  Warn,
} from "../components/contentTemplate";
import { PACTE_MIGRATOIRE_FAQ } from "../data/pacteMigratoireFaq";

// Section order mirrors the 10 H2 headings of the article body.
const SECTIONS = [
  { id: "pacte-bref",           title: "Le pacte en bref" },
  { id: "relocalisation",       title: "Le point qui fâche : la relocalisation" },
  { id: "position-polonaise",   title: "La position polonaise : ni accueil, ni paiement" },
  { id: "argument-frontiere",   title: "L'argument polonais : une frontière sous pression" },
  { id: "exemption",            title: "L'exemption obtenue" },
  { id: "loi-asile",            title: "La loi qui suspend le droit d'asile" },
  { id: "critiques",            title: "Les critiques" },
  { id: "pour-vous",            title: "Ce que cela change pour vous" },
  { id: "et-apres",             title: "Et après" },
  { id: "faq",                  title: "Questions fréquentes" },
];

const SOURCES = [
  {
    url: "https://ec.europa.eu/commission/presscorner/detail/en/ip_26_1285",
    label: "Commission européenne, le pacte entre en application le 12 juin",
    description: "Établit l'application dans tous les États membres à compter du 12 juin 2026.",
  },
  {
    url: "https://home-affairs.ec.europa.eu/news/new-migration-and-asylum-rules-enter-application-what-changing-2026-06-12_en",
    label: "Commission européenne, ce qui change avec l'entrée en application",
    description: "Décrit les dix textes, le filtrage obligatoire, la base Eurodac et les procédures accélérées à la frontière.",
  },
  {
    url: "https://www.consilium.europa.eu/en/policies/eu-migration-asylum-reform-pact/",
    label: "Conseil de l'Union européenne, pacte sur la migration et l'asile",
    description: "Établit l'adoption le 14 mai 2024 et l'entrée en application le 12 juin 2026.",
  },
  {
    url: "https://www.consilium.europa.eu/en/press/press-releases/2025/12/08/migration-and-asylum-member-states-agree-on-solidarity-pool/",
    label: "Conseil de l'Union européenne, accord sur le pool de solidarité 2026 (8 décembre 2025)",
    description: "Fixe le volume de solidarité pour 2026.",
  },
  {
    url: "https://home-affairs.ec.europa.eu/policies/migration-and-asylum/pact-migration-and-asylum/implementing-pact-migration-and-asylum_en",
    label: "Commission européenne, mise en œuvre du pacte",
    description: "Détaille les dix blocs et le cycle annuel de gouvernance de la solidarité.",
  },
  {
    url: "https://www.euronews.com/my-europe/2025/02/07/poland-will-not-implement-migration-pact-donald-tusk-tells-ursula-von-der-leyen",
    label: "Euronews, la Pologne n'appliquera pas le pacte, déclare Tusk (7 février 2025)",
    description: "Établit le refus polonais, les trois options de solidarité, les volumes de référence et l'opposition Pologne-Hongrie.",
  },
  {
    url: "https://notesfrompoland.com/2025/11/12/eu-includes-poland-among-countries-to-be-exempted-from-migrant-relocation/",
    label: "Notes from Poland, la Commission place la Pologne parmi les pays à exempter (12 novembre 2025)",
    description: "Établit la situation migratoire significative, le caractère annuel et révisable de l'exemption et les pays classés sous pression.",
  },
  {
    url: "https://notesfrompoland.com/2025/10/11/poland-says-it-will-be-exempted-from-eu-migrant-relocations/",
    label: "Notes from Poland, la Pologne annonce son exemption (11 octobre 2025)",
    description: "Établit l'annonce de l'exemption, le renforcement des règles et la visite de von der Leyen à la frontière.",
  },
  {
    url: "https://www.infomigrants.net/en/post/67538/poland-to-be-exempt-from-eu-solidarity-deal-as-future-of-migration-pact-hangs-in-balance",
    label: "InfoMigrants, la Pologne exemptée de l'accord de solidarité (octobre 2025)",
    description: "Établit la contribution de 20 000 euros par personne et le refus polonais d'accueillir et de payer.",
  },
  {
    url: "https://brusselssignal.eu/2025/10/polish-president-tells-ec-chief-his-country-will-not-accept-relocated-migrants/",
    label: "Brussels Signal, le président Nawrocki écrit à la Commission (10 octobre 2025)",
    description: "Établit la lettre de Nawrocki et l'argument de la charge ukrainienne et de la pression biélorusse.",
  },
  {
    url: "https://bisi.org.uk/reports/eu-and-migration-will-the-new-pact-hold-at-the-poland-belarus-border",
    label: "BISI, le pacte tiendra-t-il à la frontière polono-biélorusse (21 mars 2025)",
    description: "Établit l'instrumentalisation depuis 2021, les 30 090 passages en 2024 et la zone tampon de 60 kilomètres.",
  },
  {
    url: "https://www.eumigrationlawblog.eu/polish-law-restricting-the-right-to-asylum-at-borders/",
    label: "EU Migration Law Blog, la loi polonaise restreignant le droit d'asile (juillet 2025)",
    description: "Détaille l'adoption le 21 février 2025, la signature le 26 mars, l'entrée en vigueur le 27 mars, la durée de 60 jours renouvelable et les 110 595 tentatives.",
  },
  {
    url: "https://notesfrompoland.com/2025/03/27/poland-suspends-right-to-asylum-at-belarus-border/",
    label: "Notes from Poland, la Pologne suspend le droit d'asile (27 mars 2025)",
    description: "Établit la suspension de 60 jours, les exceptions pour les personnes vulnérables et le bilan de plus de cent morts depuis 2021.",
  },
  {
    url: "https://notesfrompoland.com/2025/05/27/large-fall-in-attempted-illegal-border-crossings-after-polands-asylum-ban/",
    label: "Notes from Poland, forte baisse des passages après l'interdiction de l'asile (27 mai 2025)",
    description: "Établit la baisse de 48 % et le soutien du commissaire européen.",
  },
  {
    url: "https://www.hrw.org/news/2025/02/19/poland-scrap-bill-suspending-right-asylum",
    label: "Human Rights Watch, abandonner le projet de loi suspendant le droit d'asile (19 février 2025)",
    description: "Établit les critiques sur le non-refoulement, les refoulements documentés et la zone d'exclusion fermée aux journalistes et humanitaires.",
  },
  {
    url: "https://www.amnesty.org/en/documents/eur37/9322/2025/en/",
    label: "Amnesty International, la loi suspendant les demandes d'asile viole le droit international",
    description: "Établit la position d'Amnesty sur l'incompatibilité avec le droit international et la Constitution polonaise.",
  },
  {
    url: "https://www.europarl.europa.eu/doceo/document/E-10-2025-001623_EN.html",
    label: "Parlement européen, question parlementaire E-001623/2025",
    description: "Établit les inquiétudes sur le non-refoulement et l'affaire R.A. et autres contre Pologne devant la Cour européenne des droits de l'homme.",
  },
  {
    url: "https://notesfrompoland.com/2025/10/10/polish-court-rules-asylum-ban-at-belarus-border-justified-and-lawful/",
    label: "Notes from Poland, un tribunal juge l'interdiction d'asile conforme à la Constitution (10 octobre 2025)",
    description: "Établit la décision non définitive du tribunal de Białystok et la volonté du gouvernement de reprendre certains volets du pacte mais pas la relocalisation.",
  },
];

// Shared classes for inline internal links in the body.
const INTERNAL_LINK = "text-[#0056D2] hover:underline";

// Footnote marker: <sup><a href="#source-n">[n]</a></sup>. Anchor jumps to
// the matching entry in the Sources list, which carries id="source-n".
function Note({ n }) {
  return (
    <sup data-testid={`note-${n}`} className="ml-0.5">
      <a
        href={`#source-${n}`}
        className="text-[#0056D2] hover:underline font-medium text-xs"
      >
        [{n}]
      </a>
    </sup>
  );
}

// Convert a plain-text paragraph containing [n] markers into a React fragment
// where each marker becomes a <Note n={...} />. Adjacent markers like [6][9]
// stay as distinct sup elements.
function nt(text) {
  const parts = text.split(/(\[\d+\])/g);
  return parts.map((part, idx) => {
    const m = /^\[(\d+)\]$/.exec(part);
    if (m) return <Note key={idx} n={Number(m[1])} />;
    return part;
  });
}

// Q/A pair for the inline FAQ block. Shared with the FAQPage JSON-LD payload
// declared in PAGE_SEO (SeoHead.jsx) so visible text and structured data stay
// in sync.
const FAQ = PACTE_MIGRATOIRE_FAQ;

export default function BlogPostPacteMigratoire() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="blog-post-pacte-migratoire"
    >
      <Link
        to={lp("/blog")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0056D2] mb-6"
        data-testid="blog-post-back"
      >
        <ChevronLeft className="w-4 h-4" />
        Retour au blog
      </Link>

      {/* Hero */}
      <header className="mb-10" data-testid="blog-post-hero">
        <span className="hej-chip" data-testid="blog-post-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour en juin 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Pacte migratoire de l'UE : pourquoi la Pologne refuse la relocalisation
        </h1>
      </header>

      {/* Chapeau d'introduction */}
      <section
        className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12"
        data-testid="blog-post-lede"
      >
        <p>
          {nt(
            "Depuis le 12 juin 2026, le pacte européen sur la migration et l'asile s'applique dans les vingt-sept États membres [1]. C'est la plus grande refonte des règles d'asile de l'Union depuis plus de dix ans, un paquet de dix textes qui couvre tout, du contrôle aux frontières au partage des demandeurs d'asile entre pays [2]. Sa pièce maîtresse est un mécanisme de solidarité dite obligatoire. La Pologne, qui a voté contre le pacte, a passé deux ans à combattre ce mécanisme et a fini par obtenir, dans les faits, une exemption [6]."
          )}
        </p>
        <p>
          Tout l'argument polonais tient à un endroit, sa frontière avec la Biélorussie, où Varsovie affirme que la migration est
          utilisée comme une arme. Cet article explique ce qu'est le pacte, pourquoi la Pologne refuse la relocalisation, comment
          elle a obtenu son exemption et ce que recouvre la loi polonaise qui suspend le droit d'asile à cette frontière, critiques
          comprises.
        </p>
      </section>

      {/* Sommaire auto-généré */}
      <AutoTOC sections={SECTIONS} testid="blog-post-toc" />

      {/* À retenir */}
      <KeyPointsBlock testid="blog-post-key-points">
        <p>
          Depuis le 12 juin 2026, le pacte migratoire européen s'applique dans toute l'Union, avec pour pièce maîtresse une
          solidarité obligatoire, accueillir des demandeurs d'asile, payer ou aider. La Pologne refuse la relocalisation et a
          obtenu une exemption d'accueil, qui reste annuelle et révisable, en invoquant sa frontière sous pression avec la
          Biélorussie et l'accueil des réfugiés ukrainiens. Elle garde les volets sécuritaires du pacte et maintient une loi qui
          suspend le droit d'asile à cette frontière, saluée par Varsovie et une partie de l'Union mais dénoncée par les
          organisations de droits humains. Si vous vous installez légalement en Pologne, rien de tout cela ne change votre
          parcours.
        </p>
      </KeyPointsBlock>

      {/* Infographie résumé visuel */}
      <figure className="mb-12" data-testid="blog-post-infographic">
        <img
          src="/images/blog/pacte-migratoire-pologne.png"
          alt="Infographie résumant le pacte migratoire européen et le cas de la Pologne : filtrage obligatoire, mécanisme de solidarité, exemption polonaise, suspension du droit d'asile à la frontière biélorusse et baisse des passages illégaux."
          loading="lazy"
          width="1672"
          height="941"
          className="w-full h-auto rounded-2xl border border-slate-200 shadow-sm"
        />
        <figcaption className="mt-3 text-sm text-slate-500 text-center italic">
          Le pacte migratoire européen et le cas de la Pologne, l'essentiel en un coup d'œil. Analyse de juin 2026.
        </figcaption>
      </figure>

      {/* Sections */}
      <ContentSection id="pacte-bref" n={1} title="Le pacte en bref">
        <p>
          {nt(
            "Le pacte a été adopté par le Conseil le 14 mai 2024, contre l'avis de la Pologne et de la Hongrie, puis est entré en application le 12 juin 2026 après une période de transition de deux ans [3]. Il réunit dix règlements qui harmonisent l'ensemble du parcours d'un demandeur d'asile. Toute arrivée irrégulière est désormais soumise à un filtrage obligatoire, avec contrôle d'identité, relevé biométrique enregistré dans la base Eurodac, puis examen de sécurité et de vulnérabilité [2]. Les personnes peu susceptibles d'obtenir une protection sont orientées vers une procédure accélérée à la frontière, où elles restent le temps de l'examen [2]."
          )}
        </p>
        <p>
          L'idée d'ensemble est d'uniformiser des règles jusque-là éclatées entre les États, d'accélérer les procédures et de mieux
          répartir l'effort. C'est ce dernier point qui a mis le feu aux poudres.
        </p>
      </ContentSection>

      <ContentSection id="relocalisation" n={2} title="Le point qui fâche : la relocalisation">
        <p>
          {nt(
            "Le cœur du conflit est le mécanisme de solidarité. Un pays de première entrée submergé par les arrivées peut demander de l'aide aux autres. Chaque État doit alors choisir entre trois options, accueillir une part des demandeurs d'asile, verser une contribution financière ou fournir un appui opérationnel en personnel et en matériel [6]. Le barème de référence prévoit jusqu'à 30 000 relocalisations par an à l'échelle de l'Union, ou environ 600 millions d'euros, et un pays qui refuse d'accueillir peut payer de l'ordre de 20 000 euros par personne déclinée [6][9]. Pour 2026, la première année d'application, le Conseil a fixé le volume de référence à 21 000 relocalisations, des efforts équivalents ou 420 millions d'euros [4]."
          )}
        </p>
        <p>
          {nt(
            "Les pays jugés sous pression migratoire, donc éligibles à cette solidarité, sont la Grèce, l'Italie, l'Espagne et Chypre [7]. La Pologne, elle, n'a jamais voulu figurer parmi les contributeurs."
          )}
        </p>
      </ContentSection>

      <ContentSection id="position-polonaise" n={3} title="La position polonaise : ni accueil, ni paiement">
        <p>
          {nt(
            "Dès février 2025, le Premier ministre Donald Tusk a annoncé que la Pologne n'appliquerait pas le mécanisme de relocalisation, qu'elle n'accueillerait pas de migrants relocalisés et qu'elle ne paierait pas pour s'en dispenser [6]. Sa ligne est que la relocalisation ne protège en rien l'Europe contre l'immigration illégale et que son pays se trouve dans une situation exceptionnelle [6]. Le gouvernement précise qu'il reprendra en revanche les parties du pacte qui renforcent la sécurité, comme les nouvelles procédures de retour, l'interdiction de l'instrumentalisation et les dispositifs de crise [18]."
          )}
        </p>
        <p>
          {nt(
            "Fait notable dans un pays très divisé, cette position fait consensus. Le président Karol Nawrocki, issu de l'opposition conservatrice et entré en fonction en août 2025, a écrit à la présidente de la Commission pour dire que la Pologne n'accepterait en aucun cas de migrants relocalisés [10]. Gouvernement libéral et présidence conservatrice s'opposent sur presque tout, mais pas sur ce dossier."
          )}
        </p>
      </ContentSection>

      <ContentSection id="argument-frontiere" n={4} title="L'argument polonais : une frontière sous pression">
        <p>
          {nt(
            "Pour comprendre ce refus, il faut regarder la frontière orientale. Depuis l'été 2021, Varsovie et l'Union accusent la Biélorussie et la Russie d'organiser le passage de migrants, venus surtout du Moyen-Orient, d'Asie et d'Afrique, pour déstabiliser la Pologne [11]. L'Union qualifie cette tactique d'instrumentalisation de la migration et de guerre hybride [11]. Entre juillet 2021 et novembre 2024, les gardes-frontières polonais ont recensé plus de 110 000 tentatives de passage [12]. En 2024, ils en ont intercepté environ 30 000 [11]. Un soldat polonais a été tué d'un coup de couteau à la frontière en mai 2024 [12]."
          )}
        </p>
        <p>
          {nt(
            "La Pologne a répondu par le béton et le grillage. Une barrière court sur près de 200 kilomètres, doublée d'une zone tampon interdite d'accès le long de la section de Podlasie, réintroduite en juin 2024 puis prolongée [11]. À cela s'ajoute un argument que Varsovie répète à Bruxelles, le pays accueille déjà des centaines de milliers de réfugiés ukrainiens depuis 2022, ce qui représente une charge considérable [10]."
          )}
        </p>
      </ContentSection>

      <ContentSection id="exemption" n={5} title="L'exemption obtenue">
        <p>
          {nt(
            "L'argument a fini par porter. À l'automne 2025, la Commission a placé la Pologne parmi les États qu'elle veut exempter, en tout ou partie, de l'accueil de migrants relocalisés, en reconnaissant qu'elle fait face à une situation migratoire significative [7]. Le gouvernement polonais a présenté cela comme une victoire [8]. Comme souvent, chacun s'en attribue le mérite. Tusk y voit le fruit de sa fermeté, tandis que l'opposition l'attribue à l'influence du président Nawrocki [8]."
          )}
        </p>
        <p>
          {nt(
            "Un point mérite la prudence, et l'opposition elle-même l'a souligné. Cette exemption n'est pas gravée dans le marbre. Elle découle d'un classement annuel des États selon leur situation, elle doit encore être approuvée par les États membres au sein du Conseil et elle est réexaminée chaque année [5][7]. Ce qui est acquis pour 2026 devra être reconduit ensuite."
          )}
        </p>
      </ContentSection>

      <ContentSection id="loi-asile" n={6} title="La loi qui suspend le droit d'asile">
        <p>
          {nt(
            "La contestation polonaise ne s'arrête pas à la relocalisation. En parallèle, le pays s'est doté d'un outil radical. Une loi adoptée par le Parlement le 21 février 2025, signée le 26 mars et entrée en vigueur le 27 mars, permet de suspendre temporairement le droit de déposer une demande de protection internationale à la frontière biélorusse [12]. La suspension est décidée par le gouvernement pour une durée maximale de 60 jours, renouvelable avec l'accord du Parlement [12]. Le texte introduit la notion d'instrumentalisation et vise les situations où un État voisin organise des passages [12]. Des exceptions sont prévues pour les personnes vulnérables, mineurs, femmes enceintes, personnes nécessitant des soins particuliers ou exposées à un risque réel en cas de renvoi [13]."
          )}
        </p>
        <p>
          {nt(
            "Le gouvernement met en avant son efficacité. Dans les deux mois qui ont suivi la première suspension, les tentatives de passage ont chuté de près de moitié, environ 6 100 contre 11 700 un an plus tôt [14]. Un tribunal administratif de Białystok a par ailleurs jugé, en octobre 2025, que le dispositif était conforme à la Constitution, dans une décision non définitive [18]."
          )}
        </p>
      </ContentSection>

      <ContentSection id="critiques" n={7} title="Les critiques">
        <p>
          {nt(
            "Cette loi est aussi l'objet d'une vive contestation, et l'article serait incomplet sans elle. Le Haut-Commissariat de l'ONU pour les réfugiés, Human Rights Watch, Amnesty International et la Fondation Helsinki pour les droits de l'homme estiment que suspendre le droit d'asile viole le principe de non-refoulement, le droit international et l'article 56 de la Constitution polonaise [15][16][17]. Ils affirment que la mesure donne une couverture légale à des refoulements parfois violents vers la Biélorussie [15]. Un rapport d'Oxfam et de l'association Egala documente des violences à la frontière, et la Pologne fait l'objet d'une affaire devant la Cour européenne des droits de l'homme pour des refoulements [17]. Plus de cent personnes seraient mortes aux frontières entre la Biélorussie et l'Union depuis 2021 [13]. La zone d'exclusion, qui tient à l'écart journalistes et humanitaires, et les poursuites engagées contre des bénévoles ayant porté secours, nourrissent aussi les critiques [15]."
          )}
        </p>
        <p>
          {nt(
            "La Pologne et une partie de l'Union renvoient à la nature du problème. Le commissaire européen chargé de la migration a jugé la suspension polonaise conforme au droit de l'Union et qualifié le pays de première ligne de défense de l'Europe [14]. Varsovie soutient que les règles d'asile classiques n'ont pas été conçues pour un État voisin qui fabrique délibérément une crise [14]. Le débat oppose donc deux lectures difficilement conciliables, la protection des personnes d'un côté, la sécurité d'une frontière sous attaque hybride de l'autre."
          )}
        </p>
      </ContentSection>

      <ContentSection id="pour-vous" n={8} title="Ce que cela change pour vous">
        <p>
          Si vous vous installez légalement en Pologne, l'effet direct est limité. Le pacte vise les arrivées irrégulières et
          l'asile aux frontières extérieures, pas la voie classique du visa, du permis et du titre de séjour. Et puisque la Pologne
          n'accueillera pas de relocalisés, le mécanisme de solidarité ne changera rien à votre situation. Ce dossier compte
          surtout par le climat qu'il installe, une politique migratoire centrée sur le contrôle et la fermeté. Pour le parcours
          d'installation des ressortissants hors UE, voir notre guide{" "}
          <Link to={lp("/demarches-pays-tiers")} className={INTERNAL_LINK}>
            S'installer en Pologne quand on vient d'un pays tiers
          </Link>
          . Pour le marché du travail et la réforme de 2025, voir notre article{" "}
          <Link to={lp("/blog/travailler-pologne-reforme-2025")} className={INTERNAL_LINK}>
            Travailler en Pologne après la réforme de 2025
          </Link>
          .
        </p>
      </ContentSection>

      <ContentSection id="et-apres" n={9} title="Et après">
        <p>
          {nt(
            "Le pacte est en vigueur, mais rien n'est figé. L'exemption polonaise sera réexaminée à l'été puis à l'automne 2026 et devra être reconduite chaque année [7]. À la frontière, la suspension de l'asile se prolonge tant que la pression dure, sous le regard des juridictions polonaises et européennes. Et la question de fond reste posée pour toute l'Union, un système pensé pour des arrivées par la Méditerranée peut-il répondre à une frontière terrestre transformée en levier géopolitique. La Pologne, elle, a tranché. Elle veut bien de la solidarité, à condition de la recevoir, pas de la financer."
          )}
        </p>
      </ContentSection>

      <ContentSection id="faq" n={10} title="Questions fréquentes">
        <dl className="space-y-6" data-testid="blog-post-faq">
          {FAQ.map((item, i) => (
            <div key={i} data-testid={`faq-pair-${i + 1}`}>
              <dt className="font-display text-base sm:text-lg font-semibold text-slate-900">
                {item.q}
              </dt>
              <dd className="mt-2 text-slate-700 leading-relaxed">{item.a}</dd>
            </div>
          ))}
        </dl>
      </ContentSection>

      {/* Sources */}
      <Sources sources={SOURCES} testid="blog-post-sources" />

      {/* Avertissement final */}
      <Warn testid="blog-post-warn-final">
        Cet article a une vocation d'information générale et porte sur un sujet politiquement sensible et mouvant. Les classements
        de la Commission, l'exemption polonaise et les mesures à la frontière évoluent. Reportez-vous aux sources officielles
        citées ci-dessus à la date de votre lecture.
      </Warn>
    </article>
  );
}
